import { useEffect, useState, useCallback, useRef, useReducer } from 'react';

import api, { ApiParams } from './api';
import { useApiPost, ApiResult, useApiGet } from './Api.hooks';
import { PresignedUrl, UploadStatus, FileUploadState } from './File.interface';

export interface FilePresignedDto {
  file: File;
  destinationUrl: string;
  signedUrl: string;
  expiration: number;
}

export function useSecureFileUrl(url?: string) {
  if (url) {
    return `${url}&token=${api.getAccessToken()}`;
  }
}

type UploadAction = {
  type: UploadStatus;
  payload?: any;
};
function uploadReducer(
  state: FileUploadState,
  action: UploadAction
): FileUploadState {
  switch (action.type) {
    case 'init':
      return {
        ...state,
        status: action.type,
        file: action.payload,
        progress: 0,
        error: undefined
      };
    case 'ready':
      return {
        ...state,
        status: action.type,
        presignedUrl: action.payload as PresignedUrl,
        progress: 1
      };
    case 'uploading':
      return {
        ...state,
        status: action.type,
        progress: action.payload as number
      };
    case 'error':
      return {
        ...state,
        status: action.type,
        error: action.payload
      };
    case 'complete':
      return {
        ...state,
        status: action.type,
        progress: 100,
        response: action.payload as Response
      };
    case 'cancel':
      return {
        ...state,
        status: action.type,
        file: undefined,
        progress: 0,
        error: undefined
      };
    default:
      return { ...state, status: action.type };
  }
}

type UploadCallback = (fileList: FileList | File[]) => void;

export function useUploadAttachment(
  path: string,
  token?: string
): [
  FileUploadState,
  UploadFile[] | undefined,
  UploadCallback,
  (file: File) => void
] {
  const [presignedState, getPresignedUrl] = useGetPresignedUrl(path, token);
  const requestRef = useRef(new XMLHttpRequest());
  const [
    { file, presignedUrl, response, status, progress, error },
    dispatch
  ] = useReducer<typeof uploadReducer>(uploadReducer, { progress: 0 });
  const [uploadFiles, setUploadFiles] = useState<UploadFile[]>();

  let fileIndexRef = useRef(0);
  let previosPresignedState = useRef<any>(null);

  useEffect(() => {
    if (uploadFiles && fileIndexRef.current < uploadFiles.length) {
      const request = requestRef.current;

      if (status === 'init' && file) {
        getPresignedUrl(
          uploadFiles[fileIndexRef.current].file.type ||
            'application/octet-stream'
        );
        dispatch({ type: 'presign' });
      } else if (
        status === 'presign' &&
        !presignedState.isLoading &&
        presignedState.data
      ) {
        if (previosPresignedState.current === null) {
          previosPresignedState.current = presignedState;
          dispatch({ type: 'ready', payload: presignedState.data });
        } else if (
          presignedState.data === previosPresignedState.current?.data
        ) {
          dispatch({ type: 'presign' });
        } else {
          previosPresignedState.current = presignedState;
          dispatch({ type: 'ready', payload: presignedState.data });
        }
      } else if (status === 'ready' && presignedUrl && file) {
        request.addEventListener('error', e =>
          dispatch({ type: 'error', payload: e })
        );
        request.addEventListener('timeout', e =>
          dispatch({ type: 'error', payload: e })
        );
        request.upload.addEventListener('error', e =>
          dispatch({ type: 'error', payload: e })
        );
        request.upload.addEventListener('progress', e => {
          if (e.lengthComputable) {
            dispatch({
              type: 'uploading',
              payload: (e.loaded / e.total) * 100
            });
          }
        });
        request.upload.addEventListener('load', e => {
          dispatch({ type: 'complete', payload: request.response });
        });

        request.open('PUT', presignedUrl.signedUrl);
        request.timeout = 5000;
        request.setRequestHeader('Content-Type', file.type);
        request.responseType = 'text';
        request.send(file);

        const uploadFile = uploadFiles[fileIndexRef.current];
        if (uploadFile) {
          uploadFile.progress = 100;
          uploadFile.isUploading = false;
          uploadFile.isCompleted = true;
          uploadFile.presignedResult = {
            file: uploadFile.file,
            destinationUrl: presignedUrl.storageKey,
            signedUrl: presignedUrl.signedUrl,
            expiration: presignedUrl.expiration
          };
          setUploadFiles([...uploadFiles]);
        }
        fileIndexRef.current++;
        const nextFile = uploadFiles[fileIndexRef.current];
        if (nextFile) {
          dispatch({ type: 'init', payload: nextFile.file });
        }
      } else if (status === 'cancel') {
        request.abort();
      } else {
        if (presignedState.data !== previosPresignedState.current?.data) {
          previosPresignedState.current = presignedState;
          dispatch({ type: 'ready', payload: presignedState.data });
        }
      }

      return () => {
        // clean up
        if (status === 'error' || status === 'complete') {
          request.abort();
        }
      };
    }
  }, [
    getPresignedUrl,
    presignedState,
    file,
    presignedUrl,
    status,
    error,
    uploadFiles
  ]);

  const uploadCallback = useCallback(
    (fileList: FileList | File[]) => {
      const files: UploadFile[] = [];
      for (let i = 0; i < fileList.length; i++) {
        const uploadFile = {
          file: fileList[i],
          progress: 0,
          isUploading: true,
          request: new XMLHttpRequest(),
          isCompleted: false,
          isError: false
        };

        files.push(uploadFile);
      }
      fileIndexRef.current = 0;
      setUploadFiles([...(uploadFiles || []), ...files]);
      dispatch({
        type: 'init',
        payload: [...(uploadFiles || []), ...files][0].file
      });
    },
    [uploadFiles]
  );

  // remove an upload file and abort if uploading
  const removeCallback = useCallback(
    (file: File) => {
      if (uploadFiles) {
        const index = uploadFiles.findIndex(f => f.file === file);
        if (index > -1) {
          uploadFiles[index].request.abort();
          uploadFiles.splice(index, 1);
          if (index <= fileIndexRef.current) {
            fileIndexRef.current--;
          }
          setUploadFiles([...uploadFiles]);
        }
      }
    },
    [uploadFiles]
  );

  return [
    { file, presignedUrl, status, progress, response, error },
    uploadFiles,
    uploadCallback,
    removeCallback
  ];
}

export function useGetPresignedUrl(
  path: string,
  token?: string
): [ApiResult<PresignedUrl>, (mimeType: string) => void] {
  const accessToken: string | undefined = api.getAccessToken();
  // use token to check if guest
  const guest: string = !accessToken && token ? '/guest' : '';
  const { data, response, isLoading, error, get } = useApiGet<PresignedUrl>(
    `files/presign${guest}`
  );
  const getPresignedUrl = useCallback(
    (mimeType: string) => {
      const params: ApiParams = { folderPath: path, mimeType };
      if (!accessToken && token) {
        params.token = token;
      }
      get(params);
    },
    [get, path, token, accessToken]
  );
  return [{ data, response, isLoading, error }, getPresignedUrl];
}

export function useUploadFile(path: string) {
  const {
    presignedResult,
    isLoading: isLoadingPresignedUrl,
    error: presignedError,
    presignUrl
  } = useCreatePresignedUrl(path);
  const [progress, setProgress] = useState(0);
  const [file, setFile] = useState<File>();
  const [response, setResponse] = useState<Response>();
  const [error, setError] = useState<Response>();
  const [isUploading, setIsUploading] = useState(false);
  const requestRef = useRef(new XMLHttpRequest());

  useEffect(() => {
    const request = requestRef.current;
    if (!isLoadingPresignedUrl && presignedResult && file) {
      request.upload.addEventListener('progress', event => {
        if (event.lengthComputable) {
          setProgress(event.loaded / event.total);
        }
      });

      request.upload.addEventListener('load', event => {
        setProgress(1);
        setIsUploading(false);
        setResponse(request.response);
      });

      request.upload.addEventListener('error', event => {
        setError(request.response);
        setIsUploading(false);
        setProgress(0);
      });
      request.open('PUT', presignedResult.signedUrl);
      request.setRequestHeader('Content-Type', file.type);
      request.responseType = 'text';
      request.send(file);
      return () => request.abort();
    }
  }, [file, isLoadingPresignedUrl, presignedResult]);
  const upload = useCallback(
    (file: File) => {
      setIsUploading(true);
      setProgress(0);
      setFile(file);
      presignUrl(file.type);
    },
    [presignUrl]
  );
  return {
    uploadedFile: { url: presignedResult?.destinationUrl, file },
    isUploading,
    progress,
    response,
    error: error || presignedError,
    upload
  };
}

export interface UploadFile {
  file: File;
  progress: number;
  isUploading: boolean;
  request: XMLHttpRequest;
  isCompleted?: boolean;
  isError?: boolean;
  presignedResult?: FilePresignedDto;
}

export function useUploadFiles(path: string) {
  let {
    presignedResults,
    error: presignedError,
    presignUrls
  } = useCreatePresignedUrls(path);
  let fileIndexRef = useRef(0);
  const [uploadFiles, setUploadFiles] = useState<UploadFile[]>();
  const [isUploading, setIsUploading] = useState(false);

  const upload = useCallback(
    (fileList: FileList | File[]) => {
      setIsUploading(true);
      const files: UploadFile[] = [];
      for (let i = 0; i < fileList.length; i++) {
        const uploadFile = {
          file: fileList[i],
          progress: 0,
          isUploading: true,
          request: new XMLHttpRequest(),
          isCompleted: false,
          isError: false
        };

        files.push(uploadFile);
      }
      // start upload (see first useEffect)
      setUploadFiles([...(uploadFiles || []), ...files]);
      presignUrls(files.map(u => u.file));
    },
    [presignUrls, uploadFiles]
  );

  // remove an upload file and abort if uploading
  const remove = useCallback(
    (file: File) => {
      if (uploadFiles) {
        const index = uploadFiles.findIndex(f => f.file === file);
        if (index > -1) {
          uploadFiles[index].request.abort();
          uploadFiles.splice(index, 1);
          if (index <= fileIndexRef.current) {
            fileIndexRef.current--;
          }
          setUploadFiles([...uploadFiles]);
        }
      }
    },
    [uploadFiles]
  );

  // get pre-signed url for files
  useEffect(() => {
    if (uploadFiles && fileIndexRef.current < uploadFiles.length) {
      // find presigned url matching the file to upload
      const uploadFile = uploadFiles[fileIndexRef.current];
      const presignedResult = presignedResults.find(
        p => p.file === uploadFile.file
      );
      if (!presignedResult) return; // have not yet received presigned url
      uploadFile.presignedResult = presignedResult;
      // console.log('presigned received uploadFile', uploadFiles);
      setUploadFiles([...uploadFiles]);
      fileIndexRef.current++;
      // Perform Upload
      uploadFile.request.upload.addEventListener('progress', event => {
        if (event.lengthComputable) {
          uploadFile.progress = event.loaded / event.total;
          // console.log('uploadFile progress');
          setUploadFiles([...uploadFiles]);
        }
      });
      uploadFile.request.upload.addEventListener('load', event => {
        uploadFile.progress = 1;
        uploadFile.isUploading = false;
        uploadFile.isCompleted = true;
        // console.log('uploadFile load completed');
        setUploadFiles([...uploadFiles]);
      });
      uploadFile.request.upload.addEventListener('error', event => {
        uploadFile.progress = 0;
        uploadFile.isUploading = false;
        uploadFile.isError = true;
        // console.log('uploadFile error');
        setUploadFiles([...uploadFiles]);
      });
      uploadFile.request.open('PUT', uploadFile.presignedResult.signedUrl);
      uploadFile.request.setRequestHeader('Content-Type', uploadFile.file.type);
      uploadFile.request.responseType = 'text';
      uploadFile.request.send(uploadFile.file);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presignUrls, presignedResults]);

  useEffect(() => {
    // abort upload requests when unmounted
    return () => {
      // console.log('uploadfiles ABORT');
      if (uploadFiles) {
        uploadFiles.forEach(uploadFile => uploadFile.request.abort());
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return {
    uploadFiles,
    isUploading,
    error: presignedError,
    upload,
    remove
  };
}

/**
 * Get a single presigned url
 */
export function useCreatePresignedUrl(path: string) {
  const { isLoading, post, data, error } = useApiPost<FilePresignedDto>(
    `files/presign/${path}`
  );
  const presignUrl = useCallback(
    (mimeType: string) => {
      post({ mimeType });
    },
    [post]
  );
  return { presignedResult: data, isLoading, error, presignUrl };
}

/**
 * Support getting multiple presigned urls for bulk uploading
 */
export function useCreatePresignedUrls(path: string) {
  const { isLoading, post, data, error } = useApiPost<FilePresignedDto>(
    `files/presign/${path}`
  );
  const filesRef = useRef<File[]>([]);
  const nextIndexRef = useRef(0);
  const [presignedResults, setPresignedResults] = useState<FilePresignedDto[]>(
    []
  );

  const presignUrls = useCallback(
    (files?: File[]) => {
      if (files) {
        // add files to get presigned urls
        filesRef.current.push(...files);
      }
      // get presigned url for next item in queue
      if (nextIndexRef.current < filesRef.current.length && !isLoading) {
        console.log(
          'presignedUrls',
          nextIndexRef.current,
          filesRef.current[nextIndexRef.current]
        );
        post({ mimeType: filesRef.current[nextIndexRef.current].type });
      }
    },
    [isLoading, post]
  );

  useEffect(() => {
    // check if presigned url has returned and proceed to next file
    if (data && !isLoading) {
      const file = filesRef.current[nextIndexRef.current];
      setPresignedResults([...presignedResults, { ...data, file }]);
      nextIndexRef.current++;
      presignUrls(); // process next file
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isLoading]);

  return { presignedResults, isLoading, error, presignUrls };
}
